import { useIsApiConfigured } from "@/components/providers/ApiProvider/hooks";
import { useCartManager } from "@/components/providers/CartProvider";
import { getErrorMessage } from "@/lib/lutely-api/error-manager";
import { useCheckoutControllerFulfillCheckoutSession } from "@/lib/lutely-api/gen/checkout/checkout";
import { useEffect, useRef } from "react";
import { toast } from "react-toastify";

type UseFulfillCheckoutSessionOptions = {
  sessionId?: string;
  enabled?: boolean;
  onSuccess?: () => void | Promise<void>;
  onSettled?: () => void | Promise<void>;
};

export function useFulfillCheckoutSession({
  sessionId,
  enabled = true,
  onSuccess,
  onSettled,
}: UseFulfillCheckoutSessionOptions) {
  // useMutation() rebuilds its returned object on every render (it's a spread
  // over a fresh result each time), even when nothing changed — only
  // `mutateAsync` itself is stable.
  const { mutateAsync: fulfillCheckoutSession } =
    useCheckoutControllerFulfillCheckoutSession();
  const { clearCart } = useCartManager();
  const hasRunRef = useRef<string | null>(null);

  // The callbacks are read through refs rather than depended on. By the time
  // this hook runs the customer has already been charged, so the handlers that
  // clear the cart and refresh the purchase list have to run when the request
  // resolves. Listing them as dependencies makes that conditional on every
  // caller memoising them: one inline `onSuccess={() => ...}` re-runs the
  // effect mid-flight, and the abandoned closure drops the completion
  // silently — the exact failure this hook exists to prevent. Both call sites
  // memoise correctly today; that is luck, not a guarantee.
  const onSuccessRef = useRef(onSuccess);
  const onSettledRef = useRef(onSettled);
  useEffect(() => {
    onSuccessRef.current = onSuccess;
    onSettledRef.current = onSettled;
  });

  // Tracks the component, not the effect. `isMounted` as an effect-scoped local
  // conflates "the caller went away" with "a dependency changed identity", and
  // only the first should abandon an in-flight fulfillment.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Fulfillment is an authenticated call, but this hook fires straight off a
  // Stripe redirect — before the API client necessarily has a token attached.
  // Neither call site (MainAlbumPage, purchases.tsx) accounted for that, so
  // this checks it once here instead of leaving it to every future caller.
  const isApiConfigured = useIsApiConfigured();

  useEffect(() => {
    if (
      !enabled ||
      !isApiConfigured ||
      !sessionId ||
      hasRunRef.current === sessionId
    )
      return;
    hasRunRef.current = sessionId;

    fulfillCheckoutSession({ sessionId })
      .then(async () => {
        if (!isMountedRef.current) return;
        toast.success("Purchase complete");
        clearCart();
        await onSuccessRef.current?.();
      })
      .catch((error) => {
        if (!isMountedRef.current) return;
        toast.error(
          getErrorMessage(error) ||
            "Problem completing purchase, contact support",
        );
      })
      .finally(async () => {
        if (!isMountedRef.current) return;
        await onSettledRef.current?.();
      });
  }, [sessionId, enabled, isApiConfigured, fulfillCheckoutSession, clearCart]);
}
